<script setup>
import { computed, useImageSource, useArticleAuthors } from '#imports';

const props = defineProps({
  article: {
    type: Object,
    required: true,
  },
});

const imageCaption = computed(
  () => props.article.image?.caption || `Изображение-${props.article.title}`
);

const formattedDate = computed(() => {
  const currentDate = new Date();
  const publishDate = new Date(props.article.publishedAt);

  const options = {
    month: 'long',
    day: 'numeric',
    year:
      currentDate.getFullYear() === publishDate.getFullYear()
        ? undefined
        : 'numeric',
  };

  return publishDate.toLocaleString('ru-RU', options);
});

const authors = useArticleAuthors(props.article.crew);

function getImageSource({ dpr, isMobile = false }) {
  const height = isMobile ? 192 : 187;

  return useImageSource({
    image: props.article.image,
    height,
    dpr,
  });
}
</script>

<template>
  <div class="article-item">
    <div class="article-item__image-wrapper">
      <picture v-if="article.image">
        <source
          media="(min-width: 768px)"
          :srcset="`
            ${getImageSource({ dpr: 1 })} 1x,
            ${getImageSource({ dpr: 2 })} 2x
           `"
        />
        <img
          class="article-item__image"
          :alt="imageCaption"
          width="351"
          height="192"
          :src="getImageSource({ dpr: 1, isMobile: true })"
          :srcset="`${getImageSource({
            dpr: 2,
            isMobile: true,
          })} 2x`"
        />
      </picture>
      <div v-else class="article-item__image-placeholder">
        <h3>Нет картинки</h3>
      </div>
    </div>
    <div class="article-item__info">
      <h1 class="article-item__title">
        {{ article.title }}
      </h1>
      <span class="article-item__subtitle">
        {{ article.subtitle }}
      </span>
      <div class="article-item__footer">
        <div v-if="props.article.crew" class="article-item__authors">
          <PostCardAuthors :authors="authors" is-article-item />
        </div>
        <span class="article-item__date">{{ formattedDate }}</span>
      </div>
    </div>
  </div>
</template>

<style lang="postcss" scoped>
.article-item {
  display: flex;
  gap: 16px;
  min-height: 211px;
  padding: 12px;
  background-color: rgba(var(--white-color--rgb), 70%);
  border: 1px solid rgba(17, 17, 17, 10%);
  border-radius: 16px;
  transition: var(--default-transition);

  &__image-wrapper {
    min-width: 208px;
    width: 208px;
    border-radius: 16px;
    overflow: hidden;
  }

  &__image {
    width: 100%;
    height: 100%;
    object-fit: cover;

    &-placeholder {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 100%;
    }
  }

  &__info {
    display: flex;
    flex-direction: column;
    width: 100%;
    overflow: hidden;
  }

  &__title {
    margin-bottom: 8px;
    font-size: var(--h5-font-size);
    line-height: var(--h5-line-height);
    transition: var(--default-transition);
  }

  &__subtitle {
    margin-bottom: 8px;
    font-family: var(--body-font-family);
    font-weight: 500;
    font-size: 18px;
    line-height: 24px;
    flex-grow: 1;
  }

  &__authors {
    display: flex;
    gap: 16px;
    align-items: center;
    margin-top: auto;
  }

  &__date {
    font-size: 12px;
    font-weight: 500;
    line-height: 18px;
    white-space: nowrap;
    color: rgba(var(--text-dark-color--rgb), 40%);
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  &:hover {
    background-color: rgb(var(--white-color--rgb));

    .article-item__title {
      color: var(--accent-color);
    }
  }

  @media (--small-vp) {
    flex-direction: column;
    padding: 0;
    gap: 0;

    &__image-wrapper {
      width: 100%;
      height: 192px;
    }

    &__title {
      margin-bottom: 4px;
      font-size: 16px;
      line-height: 20px;
    }

    &__subtitle {
      margin-bottom: 14px;
      font-size: 16px;
      line-height: 18px;
      overflow: hidden;
      white-space: normal;
    }

    &__info {
      padding: 8px 4px 4px 8px;
    }

    &__authors {
      flex-direction: row-reverse;
      gap: 8px;
    }
  }
}
</style>
